import { Injectable, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UserSettingsService } from './user-settings.service';

export type ThemeType = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private settingsService = inject(UserSettingsService);
  private document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  currentTheme: ThemeType = 'light';

  constructor() {
    // Theme initialization is browser-only:
    // - it depends on window.matchMedia and the user's saved settings
    // - the server-rendered HTML keeps the static default from index.html
    //   ("light-theme" on <body>), which is consistent for all users/crawlers
    if (!this.isBrowser) {
      return;
    }

    // Get user's saved theme preference
    const userSettings = this.settingsService.getCurrentSettings();
    const initialTheme = userSettings.lightTheme ? 'light' : 'dark';

    // Check system preference if no explicit setting is saved
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;

    // Apply the appropriate theme
    if (initialTheme === 'dark' || (initialTheme === 'light' && prefersDark)) {
      this.setTheme('dark');
    } else {
      this.setTheme('light');
    }
  }

  /**
   * Set a specific theme
   * @param theme - The theme to set ('light' or 'dark')
   */
  setTheme(theme: ThemeType): void {
    // Update current theme first so getTheme() is consistent on the server too
    this.currentTheme = theme;

    const body = this.document.querySelector('body');

    if (!body) {
      console.error('Body element not found');
      return;
    }

    // Remove both theme classes first
    body.classList.remove('light-theme', 'dark-theme');

    // Add the appropriate theme class
    body.classList.add(`${theme}-theme`);

    // Set color scheme for browser
    body.style.colorScheme = theme;

    // Update user settings (persisted in the browser only)
    if (this.isBrowser) {
      this.settingsService.updateSettings({ lightTheme: theme === 'light' });
    }
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Get the current theme
   * @returns The current theme
   */
  getTheme(): ThemeType {
    return this.currentTheme;
  }
}
